/* ═══════════════════════════════════════════════════════════════════════════
   Register.jsx — GWA Academy (evergreen) registration
   Route: /#/register

   ── FIXES IN THIS VERSION ────────────────────────────────────────────────
   1. Webhook URL is the APEX domain (no www) — a www→apex 301 kills the CORS
      preflight before PHP ever runs.
   2. Only 'Content-Type' is sent as a header. Authorization / X-GWA-Secret /
      X-GWA-Token were never read by the PHP and broke preflight.
   3. Secret travels in the BODY as `secret`. No ?secret= in the URL.
   4. app_slug is 'gwaacademy'
   5. Webhook response is now READ (it wasn't before) — auth_token captured.
   6. Redirects point at academy domain.
   7. The existing-Hub-user path now creates the students_gwaacademy row.
   8. Role assigned on new student creation is 'student'.
   ─────────────────────────────────────────────────────────────────────── */

import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api/nocode';
import { computeTrailStartDate } from '../../utils/campLogic';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import RadioGroup from '../../components/ui/RadioGroup';
import SafeIcon from '../../common/SafeIcon';

// TODO: swap in the real evergreen AAP product id before launch
const AAP_CHECKOUT_URL = 'https://gospelwiseauthor.com/checkout/EVERGREEN_AAP_PRODUCT_ID';

const HUB_WEBHOOK_URL = 'https://gospelwiseauthor.app/gwa-academy-register.php';
const ACADEMY_APP_URL    = 'https://academy.gospelwiseauthor.com';
const HUB_LOGIN_URL   = `https://gospelwiseauthor.app/#/login?redirect=${ACADEMY_APP_URL}`;

export default function Register() {
  const { user } = useAuth();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const plan = queryParams.get('plan');
  const enrollmentEmail = (queryParams.get('email') || '').trim().toLowerCase();
  const isSkillplateEnrollment = queryParams.get('source') === 'skillplate';

  const [activeTab, setActiveTab] = useState('new');
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [registrationOpen, setRegistrationOpen] = useState(true);
  const [checkingReg, setCheckingReg] = useState(true);
  const [authToken, setAuthToken] = useState(null);

  const [form, setForm] = useState({
    name: '', email: enrollmentEmail, password: '', genre: '', gender: '', journey_stage: '', wants_bunkmate: ''
  });
  const [existingEmail, setExistingEmail] = useState(enrollmentEmail);

  useEffect(() => {
    if (!enrollmentEmail) return;
    setForm(prev => ({ ...prev, email: enrollmentEmail }));
    setExistingEmail(enrollmentEmail);
  }, [enrollmentEmail]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await api.get('settings_gwaacademy');
        const data = Array.isArray(res) ? res : res.data || [];
        const regSetting = data.find(s => s.key === 'registration_open' || s.setting_key === 'registration_open');
        if (regSetting && regSetting.value === 'false') setRegistrationOpen(false);
      } catch (e) {
        console.error('Failed to check registration status', e);
      } finally {
        setCheckingReg(false);
      }
    };
    fetchSettings();
  }, []);

  if (user) return <Navigate to="/dashboard" replace />;

  if (checkingReg) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center p-4">
        <div className="text-forest-green text-center">
          <SafeIcon name="Loader" className="animate-spin text-4xl mb-4 mx-auto" />
          <p className="font-bold">Checking registration status...</p>
        </div>
      </div>
    );
  }

  if (!registrationOpen && !isSkillplateEnrollment) {
    return (
      <div className="min-h-screen bg-cream flex flex-col items-center justify-center p-4">
        <Card className="max-w-xl w-full text-center space-y-6 shadow-lg border-tan border-t-4 border-t-amber py-12">
          <div className="mx-auto w-16 h-16 bg-amber/20 rounded-full flex items-center justify-center mb-4 text-amber"><SafeIcon name="Lock" className="text-3xl" /></div>
          <h1 className="text-3xl font-bold text-dark-green">Registration is Closed</h1>
          <a href="https://join.crossandpen.com/p/camp-faibowrimo/"><Button className="py-4 px-10 text-lg bg-amber text-dark-green font-bold shadow-md">Join the Waitlist</Button></a>
        </Card>
      </div>
    );
  }

  /* ─────────────────────────────────────────────────────────────────────────
     Hub webhook — simple request shape. Secret in body only.
     ───────────────────────────────────────────────────────────────────────── */
  const fireHubWebhook = async (payload) => {
    const secret = import.meta.env.VITE_HUB_WEBHOOK_SECRET || 'gwa_academy_secure_sync_2026_asdasd';
    try {
      const res = await fetch(HUB_WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          secret: secret,
          app_slug: 'gwaacademy',
          ...payload,
        }),
      });

      if (!res.ok) {
        console.warn('Hub webhook returned', res.status);
        return null;
      }

      const data = await res.json();
      if (data?.auth_token) setAuthToken(data.auth_token);
      if (data?.token_error) console.warn('Hub token error:', data.token_error);
      return data;
    } catch (err) {
      console.warn('Hub webhook failed', err);
      return null;
    }
  };

  const handleSubmitNewUser = async (e) => {
    e.preventDefault();
    if (!form.genre || !form.gender || !form.journey_stage || !form.wants_bunkmate) {
      setError('Please answer all questions.');
      return;
    }
    setLoading(true);
    setError('');

    try {
      const cleanEmail = form.email.trim().toLowerCase();
      const lookupRes = await api.get('students_gwaacademy');
      const existingRows = Array.isArray(lookupRes) ? lookupRes : lookupRes?.data || [];
      const existingUser = existingRows.find(r => (r.email || '').trim().toLowerCase() === cleanEmail);

      let userId;
      const bunkmateChoice = form.wants_bunkmate === '1' ? 1 : 0;
      const trailStart = computeTrailStartDate();

      if (existingUser && existingUser.first_login_complete === true) {
        setError('An account already exists for this email.');
        setLoading(false);
        return;
      }

      if (existingUser) {
        userId = existingUser.id;
        await api.put('students_gwaacademy', userId, {
          name: form.name,
          genre: form.genre,
          gender: form.gender,
          journey_stage: form.journey_stage,
          wants_bunkmate: bunkmateChoice,
          role: existingUser.role === 'admin' ? 'admin' : 'student',
          owner_id: existingUser.owner_id || userId,
        });
      } else {
        const createRes = await api.post('students_gwaacademy', {
          name: form.name,
          email: cleanEmail,
          genre: form.genre,
          gender: form.gender,
          journey_stage: form.journey_stage,
          wants_bunkmate: bunkmateChoice,
          role: 'student',
          pass_type: plan === 'allaccess' ? 'allaccess' : 'free',
          trail_start_date: trailStart,
          first_login_complete: false,
          owner_id: 'pending',
        });
        userId = createRes?.data?.id || createRes?.id || (Array.isArray(createRes?.data) ? createRes.data[0]?.id : null);
        if (userId) await api.put('students_gwaacademy', userId, { owner_id: userId });
      }

      const hubResult = await fireHubWebhook({
        email: cleanEmail,
        name: form.name,
        full_name: form.name,
        password: form.password,
        genre: form.genre,
        gender: form.gender,
        journey_stage: form.journey_stage,
        trail_start_date: trailStart,
        source: 'academy_registration',
      });

      if (!hubResult || !hubResult.success) {
        setError('We could not reach the GospelWise Hub. Please try again, or email jeremy@crossandpen.com.');
        setLoading(false);
        return;
      }

      if (plan === 'allaccess') {
        window.location.href = `${AAP_CHECKOUT_URL}?email=${encodeURIComponent(cleanEmail)}`;
        return;
      }
      setStep(3);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleExistingSubmit = async (e) => {
    e.preventDefault();
    if (!existingEmail) {
      setError('Please enter your email address.');
      return;
    }
    setLoading(true);
    setError('');

    const cleanEmail = existingEmail.trim().toLowerCase();
    try {
      const lookupRes = await api.get('students_gwaacademy');
      const rows = Array.isArray(lookupRes) ? lookupRes : lookupRes?.data || [];
      const existing = rows.find(r => (r.email || '').trim().toLowerCase() === cleanEmail);
      const trailStart = computeTrailStartDate();

      if (!existing) {
        const createRes = await api.post('students_gwaacademy', {
          name: '',
          email: cleanEmail,
          role: 'student',
          pass_type: plan === 'allaccess' ? 'allaccess' : 'free',
          trail_start_date: trailStart,
          first_login_complete: true,
          owner_id: 'pending',
        });
        const newId = createRes?.data?.id || createRes?.id || (Array.isArray(createRes?.data) ? createRes.data[0]?.id : null);
        if (newId) await api.put('students_gwaacademy', newId, { owner_id: newId });
      }

      const hubResult = await fireHubWebhook({
        email: cleanEmail,
        action: 'grant_access',
        trail_start_date: trailStart,
        source: 'existing_hub_user',
      });

      if (!hubResult || !hubResult.success) {
        setError('We could not reach the GospelWise Hub. Please try again, or email jeremy@crossandpen.com.');
        setLoading(false);
        return;
      }

      if (plan === 'allaccess') {
        window.location.href = `${AAP_CHECKOUT_URL}?email=${encodeURIComponent(cleanEmail)}`;
      } else if (hubResult.auth_token) {
        window.location.href = `${ACADEMY_APP_URL}/?token=${encodeURIComponent(hubResult.auth_token)}`;
      } else {
        window.location.href = HUB_LOGIN_URL;
      }
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-4">
      <Card className="max-w-xl w-full text-center space-y-6 shadow-lg border-tan border-t-4 border-t-forest-green">
        <div className="mx-auto w-16 h-16 bg-forest-green rounded-full flex items-center justify-center mb-4 text-white"><SafeIcon name="Compass" className="text-3xl" /></div>
        <h1 className="text-2xl font-bold text-dark-green">{plan === 'allaccess' ? 'Register for All-Access' : 'Register for GWA Academy'}</h1>
        <div className="flex border-b border-tan mb-6">
          <button className={`flex-1 py-3 text-sm font-bold ${activeTab === 'new' ? 'border-b-2 border-forest-green text-forest-green' : 'text-muted bg-gray-50/50'}`} onClick={() => { setActiveTab('new'); setError(''); }}>New User</button>
          <button className={`flex-1 py-3 text-sm font-bold ${activeTab === 'existing' ? 'border-b-2 border-forest-green text-forest-green' : 'text-muted bg-gray-50/50'}`} onClick={() => { setActiveTab('existing'); setError(''); }}>Existing GWA Hub User</button>
        </div>
        {error && <div className="p-3 bg-red-50 border border-red-200 text-red-600 text-sm rounded-md mb-4">{error}</div>}

        {activeTab === 'new' ? (
          <>
            {step === 1 && (
              <form onSubmit={e => { e.preventDefault(); setStep(2); }} className="space-y-4 text-left">
                <Input label="Full Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
                <Input label="Email Address" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required />
                <Input label="Password" type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required />
                <Button type="submit" className="w-full py-3 text-lg">Continue <SafeIcon name="ArrowRight" className="ml-2" /></Button>
              </form>
            )}
            {step === 2 && (
              <form onSubmit={handleSubmitNewUser} className="space-y-6 text-left">
                <RadioGroup label="What do you write?" value={form.genre} onChange={val => setForm({ ...form, genre: val })} options={[{ value: 'fiction', label: 'Fiction' }, { value: 'nonfiction', label: 'Nonfiction' }]} />
                <RadioGroup label="Gender" value={form.gender} onChange={val => setForm({ ...form, gender: val })} options={[{ value: 'male', label: 'Male' }, { value: 'female', label: 'Female' }]} />
                <RadioGroup label="Journey Stage" value={form.journey_stage} onChange={val => setForm({ ...form, journey_stage: val })} options={[{ value: 'idea', label: 'Idea' }, { value: 'draft', label: 'Draft' }, { value: 'published', label: 'Published' }]} />
                <RadioGroup label="Would you like a lab partner?" value={form.wants_bunkmate} onChange={val => setForm({ ...form, wants_bunkmate: val })} options={[{ value: '1', label: 'Yes' }, { value: '0', label: 'No' }]} />
                <div className="pt-4 flex gap-3">
                  <Button type="button" variant="outline" className="w-1/3" onClick={() => setStep(1)}>Back</Button>
                  <Button type="submit" className="w-2/3 py-3 text-lg" disabled={loading}>{loading ? 'Processing...' : 'Complete Registration'}</Button>
                </div>
              </form>
            )}
            {step === 3 && (
              <div className="space-y-4 py-4"><h2 className="text-xl font-bold text-dark-green">Check your email!</h2><p className="text-muted text-sm">We've sent a link to {form.email}. Click it to activate your account.</p></div>
            )}
          </>
        ) : (
          <form onSubmit={handleExistingSubmit} className="space-y-6 text-left">
            <Input label="Hub Email Address" type="email" value={existingEmail} onChange={e => setExistingEmail(e.target.value)} required />
            <Button type="submit" className="w-full py-3 text-lg" disabled={loading}>{loading ? 'Authorizing...' : 'Grant Access & Log In'}</Button>
          </form>
        )}
      </Card>
    </div>
  );
}